/**
 * <c-accordion> — テーマ非依存のアコーディオン
 *
 * Focal の <collapsible-content> + is="toggle-button" を置き換えるための最小実装。
 * フッターのSPナビ・商品詳細の説明欄・FAQ など、開閉が要る場所で共通に使う。
 *
 * マークアップ:
 *   <c-accordion open>                      ← open を付けると最初から開く
 *     <button type="button" data-accordion-trigger aria-expanded="true">
 *       見出し
 *       <span class="c-accordion__mark" aria-hidden="true"></span>   ← ＋/− は CSS で描く
 *     </button>
 *     <div data-accordion-panel>
 *       <div data-accordion-body>中身</div>   ← 高さ計測用の内側。必須
 *     </div>
 *   </c-accordion>
 *
 * 設計メモ:
 * - 高さは panel の height を body の実寸へアニメーションさせる（auto はアニメーションしないため）
 * - 開き終わったら height:auto に戻す。中身が動的に変わっても崩れない
 * - prefers-reduced-motion のときはアニメーションしない
 * - connectedCallback で初期化するので、テーマエディタでセクションを差し替えても再初期化される
 */
if (!customElements.get('c-accordion')) {
  customElements.define(
    'c-accordion',
    class CAccordion extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-accordion-trigger]');
        this.panel = this.querySelector('[data-accordion-panel]');
        this.body = this.querySelector('[data-accordion-body]');
        if (!this.trigger || !this.panel || !this.body) return;

        this.isOpen = this.hasAttribute('open');
        this.applyState(false);

        this.onClick = this.onClick.bind(this);
        this.trigger.addEventListener('click', this.onClick);

        // 高さが変わったら開いている間だけ追従（画像の読み込み・表の折り返しなど）
        this.panel.addEventListener('transitionend', (e) => {
          if (e.propertyName === 'height' && this.isOpen) this.panel.style.height = 'auto';
        });
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onClick);
      }

      onClick() {
        this.isOpen = !this.isOpen;
        this.toggleAttribute('open', this.isOpen);
        this.applyState(true);
      }

      applyState(animate) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.trigger.setAttribute('aria-expanded', String(this.isOpen));

        if (!animate || reduce) {
          this.panel.style.height = this.isOpen ? 'auto' : '0px';
          this.panel.hidden = false;
          return;
        }

        const from = this.panel.offsetHeight;
        const to = this.isOpen ? this.body.offsetHeight : 0;

        this.panel.style.height = `${from}px`;
        // ⚠️ requestAnimationFrame は非アクティブなタブで実行されず、開かないことがある。
        //    offsetHeight を読んでリフローを強制すれば同期的に確定するので、こちらを使う。
        void this.panel.offsetHeight;
        this.panel.style.height = `${to}px`;
      }
    }
  );
}
